import { useBlocker } from "@tanstack/react-router";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@timeghost/ui/shadcn/alert-dialog";
import { Button } from "@timeghost/ui/shadcn/button";
import {
	createContext,
	type JSX,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import FormSubmitToolbar from "@/components/forms/form-submit-toolbar";
import { useEditSession } from "@/components/settings/edit-session-context";

interface EditSessionChromeProps {
	readonly children?: ReactNode;
	readonly className?: string;
	/** Return true to block leaving currentPath -> nextPath while dirty. */
	readonly shouldBlockNavigation?: (args: { readonly currentPath: string; readonly nextPath: string }) => boolean;
}

interface EditSessionLeaveApi {
	/** Resolve true if caller may continue (after discard or save). */
	readonly confirmLeave: () => Promise<boolean>;
	readonly dirty: boolean;
}

const EditSessionLeaveContext = createContext<EditSessionLeaveApi | null>(null);

export const useEditSessionLeave = (): EditSessionLeaveApi => {
	const ctx = useContext(EditSessionLeaveContext);
	if (!ctx) {
		throw new Error("useEditSessionLeave requires EditSessionChrome");
	}
	return ctx;
};

export const useEditSessionLeaveOptional = (): EditSessionLeaveApi | null => useContext(EditSessionLeaveContext);

type PendingLeave =
	| { readonly kind: "router"; readonly proceed: () => void; readonly reset: () => void }
	| {
			readonly kind: "local";
			readonly resolve: (ok: boolean) => void;
	  };

/**
 * Floating Discard/Save pill + TanStack leave blocker (save / leave / stay).
 * Wrap page content inside EditSessionProvider.
 */
export default function EditSessionChrome({
	children,
	className,
	shouldBlockNavigation,
}: EditSessionChromeProps): JSX.Element {
	const { t } = useTranslation();
	const session = useEditSession();
	const [leaveOpen, setLeaveOpen] = useState(false);
	const [leaveBusy, setLeaveBusy] = useState(false);
	const pendingRef = useRef<PendingLeave | null>(null);

	const openLeave = useCallback((pending: PendingLeave) => {
		pendingRef.current = pending;
		setLeaveOpen(true);
	}, []);

	const { proceed, reset, status } = useBlocker({
		shouldBlockFn: ({ current, next }) => {
			if (!session.dirty) {
				return false;
			}
			const currentPath = current.pathname;
			const nextPath = next.pathname;
			if (shouldBlockNavigation) {
				return shouldBlockNavigation({ currentPath, nextPath });
			}
			return currentPath !== nextPath;
		},
		enableBeforeUnload: session.dirty,
		withResolver: true,
	});

	useEffect(() => {
		if (status === "blocked") {
			openLeave({ kind: "router", proceed, reset });
		}
	}, [status, proceed, reset, openLeave]);

	const closeStay = useCallback(() => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		setLeaveOpen(false);
		if (pending?.kind === "router") {
			pending.reset();
		} else if (pending?.kind === "local") {
			pending.resolve(false);
		}
	}, []);

	const leaveWithoutSave = useCallback(() => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		session.discardAll();
		setLeaveOpen(false);
		if (pending?.kind === "router") {
			pending.proceed();
		} else if (pending?.kind === "local") {
			pending.resolve(true);
		}
	}, [session]);

	const saveAndLeave = useCallback(async () => {
		if (!session.canSave) {
			return;
		}
		setLeaveBusy(true);
		try {
			await session.saveAll();
			const pending = pendingRef.current;
			pendingRef.current = null;
			setLeaveOpen(false);
			if (pending?.kind === "router") {
				pending.proceed();
			} else if (pending?.kind === "local") {
				pending.resolve(true);
			}
		} catch {
			// stay; section toasts handle errors
		} finally {
			setLeaveBusy(false);
		}
	}, [session]);

	const confirmLeave = useCallback((): Promise<boolean> => {
		if (!session.dirty) {
			return Promise.resolve(true);
		}
		return new Promise<boolean>((resolve) => {
			openLeave({ kind: "local", resolve });
		});
	}, [openLeave, session.dirty]);

	const leaveApi = useMemo(
		(): EditSessionLeaveApi => ({ confirmLeave, dirty: session.dirty }),
		[confirmLeave, session.dirty]
	);

	return (
		<EditSessionLeaveContext.Provider value={leaveApi}>
			{children}
			<FormSubmitToolbar
				canSave={session.canSave}
				className={className}
				errors={session.errors}
				onDiscard={() => {
					session.discardAll();
				}}
				onSave={() => session.saveAll()}
				saving={session.saving || leaveBusy}
				visible={session.dirty}
			/>

			<AlertDialog
				onOpenChange={(open) => {
					if (!(open || leaveBusy)) {
						closeStay();
					}
				}}
				open={leaveOpen}>
				<AlertDialogContent className="w-full sm:max-w-md">
					<AlertDialogHeader>
						<AlertDialogTitle>{t("settings.editSession.leaveTitle")}</AlertDialogTitle>
						<AlertDialogDescription>{t("settings.editSession.leaveDescription")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="flex flex-col! gap-2">
						<AlertDialogAction
							className={"w-full"}
							disabled={!session.canSave || leaveBusy}
							loading={leaveBusy}
							onClick={() => void saveAndLeave()}>
							{t("settings.editSession.saveAndLeave")}
						</AlertDialogAction>
						<div className="grid justify-stretch gap-2 sm:grid-cols-2">
							<Button
								className={"col-1"}
								disabled={leaveBusy}
								onClick={leaveWithoutSave}
								type="button"
								variant="outline">
								{t("settings.editSession.leave")}
							</Button>
							<AlertDialogCancel disabled={leaveBusy} onClick={closeStay}>
								{t("settings.editSession.stay")}
							</AlertDialogCancel>
						</div>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</EditSessionLeaveContext.Provider>
	);
}
